import * as Api from '@ViewModels';
import * as React from 'react';
import { useEventLogging } from '../../../../models/Logging';
import { useAIConversationChatMessageQuery } from '../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { useSalesLiveCoachingConversationByIdQuery } from '../../../../queries/AIConversation/useSalesLiveCoachingConversationsQuery';
import { getCreatorLabel } from '../../CoffeeAICoachingReview/creatorLabel';
import { useCoachLabCache } from './labCache';
import {
	type CoachLabNoteKind,
	GAME_PLAN_ASK,
	gamePlanHandoff,
	hasGamePlan,
	type IRealCallTurn,
	noteText,
	parseCoachedCall,
	replayRequest,
} from './labLogic';
import { useCoachLabTransport } from './useCoachLabTransport';

type TurnFeedback = Pick<IRealCallTurn, 'notes' | 'shouldHaveSaid' | 'sandboxCard'>;

const PLAN_MIN_LENGTH = 300;

/**
 * A rep's real coached call, card by card, for a manager to give feedback on, with the option to ask their own sandbox
 * coach what it would have shown at any card. The call itself comes from the same queries AI Coaching Review uses; only
 * the manager's feedback lives here.
 */
export const useCoachLabReview = (conversationId: string, sandboxCoachId: string | undefined) => {
	const transport = useCoachLabTransport();
	const { cachedPlan, savePlan } = useCoachLabCache();
	const { logApiError } = useEventLogging('CoachLab');
	const callView = useSalesLiveCoachingConversationByIdQuery({ id: conversationId });
	const messages = useAIConversationChatMessageQuery({ enabled: !!conversationId, id: conversationId });
	const [feedback, setFeedback] = React.useState<Record<number, TurnFeedback>>({});
	const [comparing, setComparing] = React.useState<number[]>([]);
	const run = React.useRef(new AbortController());
	const leadContext = React.useRef<Promise<string | undefined> | null>(null);

	React.useEffect(() => {
		const controller = new AbortController();
		run.current = controller;
		return () => controller.abort();
	}, []);

	const baseTurns = React.useMemo(
		() => parseCoachedCall((messages.data ?? []).map(m => ({ id: m.id, role: m.role, text: m.text }))),
		[messages.data]
	);
	const turns = baseTurns.map(t => ({ ...t, ...feedback[t.number], notes: feedback[t.number]?.notes ?? [] }));

	const conversation = callView.data?.conversation;
	const meta = callView.data
		? {
				callDate: conversation?.creationDate ? new Date(conversation.creationDate).toLocaleString() : undefined,
				companyId: callView.data.company?.id,
				leadLabel: callView.data.company?.companyName || 'this lead',
				liveCoach: conversation?.agentTemplateName || conversation?.agentTemplateId || 'the live coach',
				rep: getCreatorLabel(conversation?.creator),
			}
		: undefined;

	const phase =
		callView.isPending || messages.isPending
			? 'loading'
			: callView.isError || messages.isError || !meta
				? 'failed'
				: baseTurns.length === 0
					? 'empty'
					: 'ready';

	const update = (number: number, change: (turn: TurnFeedback) => Partial<TurnFeedback>) =>
		setFeedback(prev => {
			const current = prev[number] ?? { notes: [] };
			return { ...prev, [number]: { ...current, ...change(current) } };
		});

	const contextFor = (companyId: string, signal: AbortSignal) => {
		leadContext.current ??= transport.leadContext(companyId, signal).catch((): undefined => {
			// A failed lookup is not remembered, so the next comparison tries again.
			leadContext.current = null;
			return undefined;
		});
		return leadContext.current;
	};

	/** Opens a fresh sandbox conversation on the lead and gives it the lead's Game Plan, the way a live call starts. */
	const openSandbox = async (coachId: string, companyId: string | undefined, signal: AbortSignal) => {
		const id = await transport.createConversation(coachId, signal, companyId);
		if (!companyId) return id;
		const plan = cachedPlan(companyId);
		if (plan) {
			await transport.send(id, gamePlanHandoff(plan), signal);
			return id;
		}
		// The server answers this ask with the lead's pre-cached plan when there is one.
		const opened = await transport.send(id, GAME_PLAN_ASK, signal);
		if (hasGamePlan(opened) && opened.length > PLAN_MIN_LENGTH) savePlan(companyId, opened);
		return id;
	};

	/**
	 * Several cards can be compared at once, each streaming into its own card, so the pending state is the set of card
	 * numbers in flight rather than one mutation's status.
	 */
	const compare = async (number: number) => {
		if (!sandboxCoachId || !meta) return;
		const { signal } = run.current;
		setComparing(prev => [...prev, number]);
		update(number, () => ({ sandboxCard: '' }));
		try {
			const context = meta.companyId ? await contextFor(meta.companyId, signal) : undefined;
			const sandbox = await openSandbox(sandboxCoachId, meta.companyId, signal);
			const index = baseTurns.findIndex(t => t.number === number);
			const card = await transport.send(sandbox, replayRequest(baseTurns, index, context), signal, soFar =>
				update(number, () => ({ sandboxCard: soFar }))
			);
			update(number, () => ({ sandboxCard: card || '(Your coach sent no card here.)' }));
		} catch (error) {
			if (signal.aborted) return;
			logApiError('CompareCard-Error', error as Api.IOperationResultNoValue);
			update(number, () => ({ sandboxCard: '(Could not get a card from your coach. Try again.)' }));
		} finally {
			setComparing(prev => prev.filter(n => n !== number));
		}
	};

	const addNote = (number: number, kind: CoachLabNoteKind, text: string) => {
		if (!text.trim()) return;
		update(number, t => ({ notes: [...t.notes, noteText(kind, text)] }));
	};

	const removeNote = (number: number, index: number) =>
		update(number, t => ({ notes: t.notes.filter((_, i) => i !== index) }));

	const setShouldHaveSaid = (number: number, text: string) =>
		update(number, () => ({ shouldHaveSaid: text.trim() || undefined }));

	const buildId = () => (sandboxCoachId ? transport.buildId(sandboxCoachId) : Promise.resolve(''));

	return { addNote, buildId, compare, comparing, meta, phase, removeNote, setShouldHaveSaid, turns };
};
